import type { ThemePreset } from './ThemePreset';
import { BUILT_IN_THEMES } from './presets';

const THEME_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * ThemeRegistry — the single lookup for named themes (Manager pattern).
 *
 * Built-in presets are registered on first access; hosts may add more (a customer
 * theme loaded from storage, a tenant default) via `register`. Names are stable
 * identifiers that end up in customer markdown, so they are validated and never
 * silently rewritten.
 */
export class ThemeRegistry {
    private static _instance: ThemeRegistry | null = null;

    public static get Instance(): ThemeRegistry {
        if (!ThemeRegistry._instance) ThemeRegistry._instance = new ThemeRegistry();
        return ThemeRegistry._instance;
    }

    private readonly _presets = new Map<string, ThemePreset>();

    private constructor() {
        for (const preset of BUILT_IN_THEMES) this.register(preset);
    }

    /** Register or replace a preset. Throws on an invalid name: a typo here would be a customer-facing bug. */
    public register(preset: ThemePreset): void {
        if (!THEME_NAME_PATTERN.test(preset.name)) {
            throw new Error(`ThemeRegistry: invalid theme name "${preset.name}" (expected kebab-case)`);
        }
        this._presets.set(preset.name, { ...preset, values: { ...preset.values } });
    }

    public has(name: string): boolean {
        return this._presets.has(ThemeRegistry.normalize(name));
    }

    public get(name: string): ThemePreset | null {
        return this._presets.get(ThemeRegistry.normalize(name)) ?? null;
    }

    public names(): string[] {
        return [...this._presets.keys()];
    }

    public all(): ThemePreset[] {
        return [...this._presets.values()];
    }

    /** Markdown authors write by hand; accept surrounding whitespace and case, nothing else. */
    public static normalize(name: string): string {
        return name.trim().toLowerCase();
    }
}
